import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormControl } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { NbDialogService } from '@nebular/theme';
import { Subscription } from 'rxjs';

import { CvDocumentsHubDialogComponent } from '../../cv/cv-documents-hub/cv-documents-hub-dialog.component';
import { CvListComponent } from '../../cv/cv-list/cv-list.component';
import { User } from '../../interfaces/user';
import { AuthService } from '../../shared/auth.service';
import { Globals } from '../../shared/Globals';

/**
 * CVs sub-route under /clients/:id and /company. Hosts the full-width
 * CV list plus a subject picker + Generate CV button. The Generate
 * button opens a dialog-hosted generate panel (wired in Task 4 of
 * 2026-04-23-cv-ux-reshape.md).
 */
@Component({
  selector: 'bst-client-cvs-tab',
  templateUrl: './client-cvs-tab.component.html',
  styleUrls: ['./client-cvs-tab.component.scss'],
  standalone: false,
})
export class ClientCvsTabComponent implements OnInit, OnDestroy {

  public clientId: number | null = null;
  // True on the standalone /cv-generator route (set via route data), false when
  // hosted as the CVs tab inside client-detail / company. Drives the page header.
  public isWorkspace = false;
  // bst-list-adder is single-mode; emits the picked User row or null.
  public userControl = new FormControl<User | null>(null);
  // Toggle between the standard GeneratedCv list and the tailored packages list.
  // Defaults to 'tailored' for non-admins: the standard-CV endpoint is
  // admin-only (CvController::listForClient), so a staffer landing on
  // 'standard' would hit a 403.
  public docView: 'standard' | 'tailored' = this.auth.isAdmin ? 'standard' : 'tailored';

  @ViewChild(CvListComponent) protected cvList?: CvListComponent;

  private subs: Subscription[] = [];

  constructor(
    private route: ActivatedRoute,
    public auth: AuthService,
    private dialog: NbDialogService,
  ) {}

  ngOnInit(): void {
    this.isWorkspace = !!this.route.snapshot.data['cvWorkspace'];
    // Parent route owns :id; /company has no id param so fall back to the
    // auth user's own client id.
    const parent = this.route.parent;
    if (parent) {
      this.subs.push(parent.paramMap.subscribe(params => {
        const raw = params.get('id');
        this.clientId = raw ? parseInt(raw, 10) : (this.auth.user.getValue()?.client?.id ?? null);
        // Subject is roster-scoped to clientId; clear on client change to
        // avoid cross-client Generate.
        this.userControl.setValue(null);
      }));
    }
  }

  ngOnDestroy(): void {
    this.subs.forEach(s => s.unsubscribe());
    this.subs = [];
  }

  public get selectedUserId(): number | null {
    return this.userControl.value?.id ?? null;
  }

  /**
   * True when the caller can manage the CV theme for this client. Same
   * gate as the previous canSeeCvSection on ClientComponent: admin of
   * their own client.
   */
  public get canManageTheme(): boolean {
    const me = this.auth.user.getValue();
    return !!this.clientId && !!me && this.auth.isAdmin && me.client?.id === this.clientId;
  }

  // Studio link: hosted as a child of the parent route, so external clients land
  // at /clients/:id/cvs/studio (keeping the CVs tab highlighted via
  // activeLinkOptions.exact=false) while the top-level CV Generator surface
  // routes to /cv-generator/studio.
  public get studioLink(): unknown[] {
    const id = this.route.parent?.snapshot.paramMap.get('id');
    if (id) return ['/clients', id, 'cvs', 'studio'];
    return ['/cv-generator', 'studio'];
  }

  public userLogo(user: any): string | null {
    return user?.logo?.preview ? Globals.storage(user.logo.preview) : user?.provider_avatar || null;
  }

  public openGenerateDialog(): void {
    if (!this.selectedUserId || !this.clientId) {
      return;
    }

    const ref = this.dialog.open(CvDocumentsHubDialogComponent, {
      context: {
        userId: this.selectedUserId,
        clientId: this.clientId,
      },
      closeOnBackdropClick: false,
      closeOnEsc: true,
      autoFocus: true,
      hasBackdrop: true,
    });

    ref.onClose.subscribe((result: { generated: boolean } | undefined) => {
      if (result?.generated) {
        // bst-dual-axis owns paging now; reset() reloads from page 1.
        void this.cvList?.dualAxis?.reset();
      }
    });
  }
}
